import { TRtkErrorType, useTranslation } from '@packages/utils';
import { useState, useEffect } from 'react';
import PageWrapper from 'components/Base/PageWrapper';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import { Tabs } from 'components/Base/Tabs';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { useAppSelector } from 'redux/store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutesPaths } from 'routes/Routes.types';
import { useGetElcByIdQuery } from 'redux/apiSlice/elcs.slice';
import { useGetELCLightBoxQuery } from 'redux/apiSlice/lightbox.slice';
import { useCheckPermission } from 'hooks/useCheckPermission';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import ELCLightboxModal from 'components/Shared/ELCLightboxModal';
import Notes from 'components/Shared/Notes';
import { ENoteEntityType } from 'data/types/notes.types';
import OutputTab from 'components/Shared/OutputTab';
import { EELSDetailsTabsUrl } from '../components/ELSDetailTabs.types';
import DetailsTab from '../components/DetailsTab';
import { TELCDetailProps } from './ELCDetail.types';
import Contacts from '../components/ContactsTab';
import ElacMembers from '../components/ELACMembersTab';
import ELCPlaces from '../components/ELCPlacesTab';
import Meetings from '../components/MeetingsTab';
import Resources from '../components/Resources';
import InKindTab from '../components/InKindTab';

const ELCDetail = ({
  isDetailsEditable,
  backButtonSuffix,
  backUrl,
  initialTab,
  extraActions,
}: TELCDetailProps) => {
  const isElcsMoreThanOne = useAppSelector(
    state => state.redirectSlice.isElcsMoreThanOne,
  );
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs',
  });
  const isContactVisible = useCheckPermission('ELC_CONTACTS_VIEW');
  const isPlacesVisible = useCheckPermission('ELC_PLACES_VIEW');
  const isMeetingsTabVisible = useCheckPermission('MEETING_VIEW');
  const isResourcesTabVisible = useCheckPermission('ELC_RESOURCE_VIEW');
  const params = useParams();
  const { id } = params as { id: string };
  const navigate = useNavigate();
  const location = useLocation();
  const goBackState = (location.state as TBackButtonState) || {
    backUrl,
    backButtonSuffix,
  };

  const { data: elcLightboxData } = useGetELCLightBoxQuery();
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    if (
      elcLightboxData &&
      elcLightboxData.published &&
      !sessionStorage.getItem('elcLightboxSeen')
    ) {
      setShowLightbox(true);
    }
  }, [elcLightboxData]);

  const handleCloseLightbox = () => {
    sessionStorage.setItem('elcLightboxSeen', 'true');
    setShowLightbox(false);
  };

  const onBackClick = () => {
    if (!goBackState?.backUrl) {
      return;
    }
    navigate(goBackState.backUrl);
  };

  const { data, isLoading, error } = useGetElcByIdQuery({
    elcId: id,
  });

  const isOutputsRoute = location.pathname.endsWith('/inputs-outputs');
  const { tabInUrl } = useKeepTabsInUrl({
    defaultTab:
      initialTab ||
      (isOutputsRoute ? EELSDetailsTabsUrl.OUTPUT : EELSDetailsTabsUrl.DETAILS),
    stateInUrl: goBackState,
  });

  const handleTabChange = (key: string) => {
    if (!id) return;
    const isMyElc = location.pathname.includes('/my-elc/');
    const baseDetailPath = isMyElc
      ? `/app/${RoutesPaths.MYELCS_DETAIL}`.replace(':id', id)
      : `/app/${RoutesPaths.ELC_DETAIL}`.replace(':id', id);
    if (key === EELSDetailsTabsUrl.OUTPUT) {
      navigate(`${baseDetailPath}/inputs-outputs`, { state: goBackState });
    } else {
      navigate(`${baseDetailPath}?tab=${key}`, { state: goBackState });
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: EELSDetailsTabsUrl.DETAILS,
      label: t('detail.tabs.DETAILS'),
      children: (
        <DetailsTab
          isEditable={isDetailsEditable || false}
          data={data}
          elcId={id}
        />
      ),
    },
    {
      key: EELSDetailsTabsUrl.CONTACTS,
      label: t('detail.tabs.CONTACTS'),
      children: <Contacts elcId={id} />,
      show: isContactVisible,
    },
    {
      key: EELSDetailsTabsUrl.ELAC_MEMBERS,
      label: t('detail.tabs.ELAC_MEMBERS'),
      children: <ElacMembers elcId={id} />,
      show: isContactVisible,
    },
    {
      key: EELSDetailsTabsUrl.ELC_PLACES,
      label: t('detail.tabs.ELC_PLACES'),
      children: <ELCPlaces elcId={id} />,
      show: isPlacesVisible,
    },
    {
      key: EELSDetailsTabsUrl.RESOURCES,
      label: <span data-cy="resources_tab">{t('detail.tabs.RESOURCES')}</span>,
      children: <Resources elcId={id} />,
      show: isResourcesTabVisible,
    },
    {
      key: EELSDetailsTabsUrl.MEETINGS,
      label: <span data-cy="meetings_tab">{t('detail.tabs.MEETINGS')}</span>,
      children: <Meetings elcId={id} />,
      show: isMeetingsTabVisible,
    },
    {
      key: EELSDetailsTabsUrl.NOTES,
      label: <span data-cy="notes_tab">{t('detail.tabs.NOTES', 'Notes')}</span>,
      children: <Notes entityType={ENoteEntityType.ELC} entityId={id} />,
    },
    {
      key: EELSDetailsTabsUrl.OUTPUT,
      label: (
        <span data-cy="tab_output">{t('detail.tabs.OUTPUT', 'Outputs')}</span>
      ),
      children: (
        <OutputTab id={id} sourceType="ELC" isWorkshopOrEvent={false} />
      ),
    },
    {
      key: EELSDetailsTabsUrl.IN_KIND,
      label: <span data-cy="tab_in_kind">In-Kind</span>,
      children: <InKindTab elcId={id} />,
    },
  ];

  return (
    <>
      <PageWrapper
        title={data?.name}
        {...(goBackState?.backButtonSuffix &&
          isElcsMoreThanOne && {
            backButton: {
              labelPrefix: data?.name || '',
              tooltip: goBackState.backButtonSuffix,
              onClick: onBackClick,
            },
          })}
        isLoading={isLoading}
        error={data ? undefined : (error as TRtkErrorType)}
        actions={extraActions}
      >
        <Tabs
          defaultActiveKey={tabInUrl}
          onChange={handleTabChange}
          items={tabItems}
          destroyInactiveTabPane
        />
      </PageWrapper>
      {elcLightboxData && showLightbox && (
        <ELCLightboxModal
          open={showLightbox}
          onClose={handleCloseLightbox}
          lightboxData={elcLightboxData}
        />
      )}
    </>
  );
};

export default ELCDetail;
